import type { SupplierOrderDto, SupplierOrderLineDto } from '../../../../lib/api/v2/planning'
import { computeStatusCounts, readiness } from './supplier-orders-helpers'

/**
 * The Commandes section phase (SOLY-PO-001 — progressive disclosure, mirroring Métré/Pose mini-scope 29).
 * A fresh project shows ONLY the empty-state choices; the work surface and reminders appear once there
 * is something to order; a fully-received project collapses to a single serene line.
 *
 *   - `a-definir` : no live line AND no « rien à commander » marker — nothing decided yet.
 *   - `rien`      : the « rien à commander » marker is set (the C1 exit).
 *   - `en-cours`  : ≥1 live line, not all received — the work surface.
 *   - `recu`      : ≥1 live line, all received — « tout reçu · prêt pour la pose ».
 */
export type CommandesPhase = 'a-definir' | 'rien' | 'en-cours' | 'recu'

export function deriveCommandesPhase(
  lines: SupplierOrderLineDto[],
  _orders: SupplierOrderDto[],
  nothingToOrderAt: string | null,
): CommandesPhase {
  const counts = computeStatusCounts(lines)
  // Live = everything not cancelled (cancelled lines are history, never drive the phase).
  const liveCount = counts.toOrder + counts.onHold + counts.ordered + counts.confirmed + counts.received

  if (liveCount === 0) {
    // The marker only matters when there is nothing live: a real line always wins over the marker
    // (e.g. a line added after a « rien à commander » was mistakenly set re-opens the work surface).
    return nothingToOrderAt ? 'rien' : 'a-definir'
  }

  const r = readiness(counts)
  return r.total > 0 && r.received === r.total ? 'recu' : 'en-cours'
}
